import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const BRAND_NAVY = "#002FA7";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
          <Link to="/landing" className="font-serif text-xl font-bold tracking-tight" style={{ color: BRAND_NAVY }}>
            BilloraLaw
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-serif font-bold mb-2" style={{ color: BRAND_NAVY }}>
          Términos de Uso y Condiciones
        </h1>
        <p className="text-sm text-slate-400 mb-10">Última actualización: Mayo 2026</p>

        <div className="space-y-6 text-sm text-slate-700 leading-relaxed [&_h2]:text-xl [&_h2]:font-serif [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-800 [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1 [&_hr]:my-8 [&_hr]:border-slate-200 [&_p]:mb-3 [&_strong]:text-slate-900 [&_a]:text-[#002FA7] [&_a]:underline">

          <p className="uppercase text-xs font-semibold text-slate-500 tracking-wider">
            ESTOS TÉRMINOS Y CONDICIONES ("Términos") ESTABLECEN LOS TÉRMINOS LEGALES Y VINCULANTES BAJO LOS CUALES BILLORA LAW PROPORCIONA SU PLATAFORMA DE GESTIÓN DE BILLING Y PROYECTOS PARA ESTUDIOS JURÍDICOS. AL ACCEDER Y UTILIZAR BILLORA LAW, USTED ACEPTA ESTAR VINCULADO POR ESTOS TÉRMINOS.
          </p>

          <hr />

          <h2>1. Definiciones</h2>
          <ul>
            <li><strong>"Billora Law" o "Nosotros"</strong> = Billora Law SpA, empresa chilena que proporciona la plataforma SaaS.</li>
            <li><strong>"Plataforma"</strong> = El sitio web billoralaw.com, la aplicación web y todas las funcionalidades, características, contenidos y servicios asociados.</li>
            <li><strong>"Usuario"</strong> = Toda persona que accede y utiliza la Plataforma, ya sea como administrador, profesional, empleado o personal de una firma de abogados.</li>
            <li><strong>"Firma" o "Cliente"</strong> = El estudio jurídico o despacho de abogados que contrata los servicios de Billora Law.</li>
            <li><strong>"Contenido"</strong> = Toda información, datos, documentos, texto, imágenes, proyectos, actividades, información de facturación y cualquier material subido o generado en la Plataforma.</li>
            <li><strong>"Datos Personales"</strong> = Información que identifica o puede identificar a una persona física, conforme a la Ley 19.628.</li>
            <li><strong>"Datos de la Firma"</strong> = Información de clientes, proyectos, horas trabajadas, montos facturables y toda otra información que la Firma ingresa o genera en la Plataforma.</li>
          </ul>

          <hr />

          <h2>2. Aceptación de Términos</h2>
          <p>Estos Términos constituyen un contrato vinculante entre Billora Law y la Firma cliente.</p>
          <p>Al crear una cuenta en Billora Law, hacer clic en "Aceptar" o continuar usando la Plataforma, usted reconoce que:</p>
          <ul>
            <li>Ha leído y comprendido estos Términos</li>
            <li>Acepta estar vinculado por ellos</li>
            <li>Tiene autoridad legal para aceptarlos en nombre de la Firma</li>
          </ul>
          <p>Si no está de acuerdo con estos Términos, no debe acceder ni utilizar la Plataforma.</p>
          <p>Billora Law se reserva el derecho de actualizar estos Términos en cualquier momento. Cambios materiales serán notificados por email con al menos 30 días de anticipación.</p>

          <hr />

          <h2>3. Acceso y Uso de la Plataforma</h2>
          <h3>3.1 Licencia limitada</h3>
          <p>Billora Law le otorga una licencia limitada, revocable, no exclusiva y no transferible para acceder y utilizar la Plataforma únicamente para:</p>
          <ul>
            <li>Registrar horas trabajadas por profesionales</li>
            <li>Gestionar proyectos y actividades</li>
            <li>Generar facturas basadas en tiempo trabajado</li>
            <li>Calcular montos facturables en UF y pesos chilenos</li>
            <li>Administrar información de clientes empresariales y sus contactos</li>
            <li>Cualquier otro propósito expresamente autorizado por Billora Law</li>
          </ul>

          <h3>3.2 Restricciones de uso</h3>
          <p>Usted se compromete a NO:</p>
          <ul>
            <li>Violar cualquier ley o regulación aplicable</li>
            <li>Vender, transferir, asignar, sublicenciar, arrendar o prestar acceso a terceros no autorizados</li>
            <li>Intentar ganar acceso no autorizado a la Plataforma o sus sistemas</li>
            <li>Usar herramientas de automatización, scraping, bots o técnicas similares para extraer datos</li>
            <li>Violar derechos de propiedad intelectual de Billora Law o terceros</li>
            <li>Usar la Plataforma para fines ilegales o maliciosos</li>
            <li>Cargar virus, malware o código malicioso</li>
            <li>Interferir con o interrumpir la operación de la Plataforma</li>
            <li>Reproducir, duplicar o crear obras derivadas de la Plataforma sin autorización</li>
          </ul>

          <h3>3.3 Autorización de usuarios</h3>
          <p>La Firma es responsable de autorizar quién puede acceder, establecer contraseñas seguras, mantener la confidencialidad de credenciales, notificar cuando un usuario ya no debe tener acceso, y cualquier actividad realizada bajo sus credenciales.</p>

          <h3>3.4 Suspensión de acceso</h3>
          <p>Billora Law puede suspender o terminar acceso inmediatamente sin notificación previa si detecta violación de estos Términos, acceso no autorizado, lo requiere una orden judicial, hay incumplimiento de pago, o hay riesgo de seguridad.</p>

          <hr />

          <h2>4. Registro y Credenciales</h2>
          <p>La Firma garantiza que la información proporcionada al registrarse es exacta, completa, actual, autorizada y no infringe derechos de terceros.</p>
          <p>La Firma es responsable de mantener la confidencialidad de sus credenciales. No comparta credenciales con personas no autorizadas. Notifique a Billora Law inmediatamente de acceso no autorizado.</p>
          <p>La Firma puede crear múltiples cuentas de usuario dentro de su suscripción. Cada usuario debe tener su propia autenticación.</p>

          <hr />

          <h2>5. Propiedad Intelectual</h2>
          <h3>5.1 Derechos de Billora Law</h3>
          <p>Billora Law retiene todos los derechos, títulos e intereses en la Plataforma, incluyendo código, diseño, características, funcionalidades, documentación, manuales, materiales de capacitación, mejoras y derivados.</p>

          <h3>5.2 Derechos de la Firma</h3>
          <p>La Firma retiene todos los derechos sobre datos que ingresa en la Plataforma (información de clientes, horas, proyectos, etc.) y contenido original creado por la Firma.</p>

          <h3>5.3 Licencia sobre Datos de la Firma</h3>
          <p>Al ingresar Datos en Billora Law, usted otorga una licencia para procesar y almacenar los Datos, usar Datos agregados y anonimizados para mejorar la Plataforma, usar Datos para análisis técnico y operacional, y crear respaldos. Billora Law no venderá ni compartirá Datos con terceros sin consentimiento.</p>

          <h3>5.4 Retroalimentación</h3>
          <p>Si proporcionas feedback, sugerencias o mejoras sobre la Plataforma, Billora Law puede usarlos libremente sin obligación de compensarte.</p>

          <hr />

          <h2>6. Responsabilidades de la Firma</h2>
          <h3>6.1 Cumplimiento normativo</h3>
          <p>La Firma es responsable de cumplir con todas las leyes y regulaciones aplicables, obligaciones tributarias (SII), obligaciones laborales (Código del Trabajo, DT), regulaciones contables de Chile, y protección de datos según Ley 19.628.</p>

          <h3>6.2 Información sensible</h3>
          <p>La Firma se compromete a no ingresar documentos legales completos o confidenciales en Billora, segregar información sujeta a secreto profesional, usar Billora solo para información administrativa y de facturación, y no comprometer el secreto profesional con sus clientes.</p>

          <h3>6.3 Integridad de datos</h3>
          <p>La Firma es responsable de asegurar que datos ingresados son precisos y completos, mantener respaldos de información crítica, y notificar errores o inconsistencias detectadas.</p>

          <hr />

          <h2>7. Limitación de Responsabilidad</h2>
          <h3>7.1 Sin garantías</h3>
          <p className="uppercase text-xs font-semibold text-slate-500">LA PLATAFORMA SE PROPORCIONA "COMO ESTÁ" SIN GARANTÍAS DE NINGÚN TIPO. BILLORA LAW RECHAZA TODAS LAS GARANTÍAS, EXPRESAS E IMPLÍCITAS, INCLUYENDO GARANTÍAS DE COMERCIABILIDAD, IDONEIDAD PARA UN PROPÓSITO PARTICULAR Y NO INFRACCIÓN.</p>

          <h3>7.2 Disponibilidad</h3>
          <p>Aunque nos esforzamos por mantener alta disponibilidad, Billora Law NO garantiza disponibilidad continua, ausencia de interrupciones, ni funcionamiento sin errores.</p>

          <h3>7.3 Límite de responsabilidad</h3>
          <p className="uppercase text-xs font-semibold text-slate-500">EN LA MÁXIMA MEDIDA PERMITIDA POR LEY, LA RESPONSABILIDAD TOTAL DE BILLORA LAW NO EXCEDERÁ EL MONTO TOTAL PAGADO POR LA FIRMA EN LOS ÚLTIMOS 12 MESES.</p>
          <p>Billora Law no será responsable por daños indirectos, incidentales, especiales, consecuentes o punitivos, pérdida de ingresos, ganancias, datos o clientela, ni costos de obtención de servicios substitutos.</p>

          <h3>7.4 Limitaciones sobre restricciones</h3>
          <p>Estas limitaciones no aplican a daño a la vida, cuerpo o salud, fraude o dolo, violación de leyes sobre protección del consumidor, ni negligencia grave de Billora Law.</p>

          <hr />

          <h2>8. Terminación y Cancelación</h2>
          <p>La suscripción comienza en la fecha de activación y continúa por el término acordado (mensual, anual, etc.) a menos que se cancele.</p>
          <p><strong>Cancelación por la Firma:</strong> La Firma puede cancelar en cualquier momento. La cancelación es efectiva al final del período de facturación actual. Billora Law no reembolsará pagos adelantados.</p>
          <p><strong>Cancelación por Billora Law:</strong> Puede cancelar con 30 días de aviso por cambio de Términos, inmediatamente sin aviso si hay violación grave, o por incumplimiento de pago después de 30 días de aviso.</p>
          <p><strong>Exportación de datos:</strong> Antes de la cancelación, la Firma puede solicitar exportar sus Datos en formato CSV o JSON. Billora Law proporcionará esta funcionalidad sin costo dentro de 30 días.</p>

          <hr />

          <h2>9. Pago y Facturación</h2>
          <p>Los precios están disponibles en billoralaw.com y sujetos a cambios con 30 días de aviso. Billora Law procesa pagos a través de procesadores seguros (Stripe, PayU, etc.).</p>
          <p>Precios no incluyen IVA u otros impuestos. La Firma es responsable de pagar todos los impuestos aplicables.</p>
          <p>Si el pago falla: se enviará aviso, después de 7 días se reintentará, después de 30 días sin pago se puede suspender acceso, y después de 60 días se puede cancelar la suscripción.</p>

          <hr />

          <h2>10. Política de Respaldos y Recuperación</h2>
          <p>Billora Law mantiene respaldos automáticos para recuperación ante desastres, cumplimiento de obligaciones de seguridad, y auditoría. Billora Law no garantiza recuperación instantánea de Datos eliminados. La Firma es responsable de mantener sus propios respaldos.</p>
          <p>Billora Law puede eliminar datos después de cancelación de suscripción (período de gracia antes de eliminación), período de inactividad significativa (180 días sin acceso), o terminación por violación de Términos.</p>

          <hr />

          <h2>11. Soporte Técnico</h2>
          <p>Billora Law proporciona soporte técnico por email y teléfono en horario comercial (lunes a viernes, 9:00-18:00 hora Chile).</p>
          <p><strong>Tiempos de respuesta:</strong></p>
          <ul>
            <li><strong>Críticas</strong> (acceso perdido): dentro de 4 horas hábiles</li>
            <li><strong>Altas</strong> (funcionalidades no operacionales): dentro de 8 horas hábiles</li>
            <li><strong>Normales</strong> (preguntas, mejoras): dentro de 48 horas hábiles</li>
          </ul>

          <hr />

          <h2>12. Mantenimiento y Actualizaciones</h2>
          <p>Billora Law puede realizar mantenimiento programado normalmente fuera de horario comercial con aviso previo, y mantenimiento de emergencia sin aviso para resolver problemas de seguridad críticos o interrupciones de servicio.</p>
          <p>Billora Law actualiza regularmente la Plataforma. Cambios menores son automáticos. Cambios significativos se comunican previamente cuando sea posible.</p>

          <hr />

          <h2>13. Cumplimiento Legal y Regulatorio</h2>
          <p>La Firma es responsable de cumplir con Ley 19.628 (Protección de Datos), SII, regulaciones contables, Código del Trabajo y obligaciones ante DT.</p>
          <p>Billora Law ayuda a registrar horas, calcular montos y generar facturas, pero la Firma es responsable final de enviar comprobantes al SII, pagar impuestos y mantener registros tributarios.</p>
          <p>Billora Law cooperará con órdenes judiciales legítimas y requerimientos de autoridades (SII, DT, Tribunales). Billora Law actúa como "Encargado de Tratamiento" de datos personales; la Firma actúa como "Responsable".</p>

          <hr />

          <h2>14. Confidencialidad</h2>
          <p>Cada parte reconoce como confidencial los términos comerciales, información comercial sensible, datos personales de usuarios, y datos de clientes de la Firma.</p>
          <p>Billora Law se compromete a mantener información confidencial segura, usarla solo para prestar servicios, no revelarla a terceros sin autorización, e implementar medidas de seguridad robustas.</p>
          <p>No es breach de confidencialidad revelar información si es requerido por ley, es de conocimiento público, o es necesario para proteger derechos de Billora Law.</p>

          <hr />

          <h2>15. Limitaciones Geográficas</h2>
          <p>Billora Law opera primariamente para estudios jurídicos en Chile, optimizado para regulación tributaria chilena (SII), obligaciones laborales chilenas (DT), y leyes de protección de datos chilenas (Ley 19.628).</p>
          <p>Si la Firma usa Billora Law en otras jurisdicciones, es responsable de cumplir con leyes locales. Billora Law no garantiza cumplimiento de regulaciones extranjeras.</p>

          <hr />

          <h2>16. Indemnización</h2>
          <p>La Firma indemnizará y eximirá a Billora Law de reclamaciones de terceros derivadas del uso de la Plataforma, reclamos por violación de derechos de propiedad intelectual, datos ilegales o no autorizados, violación de leyes, y reclamos de clientes de la Firma.</p>
          <p>Si Billora Law recibe reclamo, la Firma debe ser notificada inmediatamente, tener derecho a controlar la defensa, cooperar en la resolución, y pagar costos razonables de defensa.</p>

          <hr />

          <h2>17. Moderación de Contenido</h2>
          <p>La Firma no puede ingresar contenido ilegal, que viole derechos de terceros, de naturaleza sexual explícita o abusiva, que constituya acoso, amenazas o discriminación, malware o virus, ni información que vulnere secreto profesional innecesariamente.</p>
          <p>Billora Law se reserva el derecho a revisar, modificar o remover Contenido que viole estos Términos, suspender acceso, y cooperar con autoridades en investigaciones.</p>

          <hr />

          <h2>18. Cambios a la Plataforma</h2>
          <p>Billora Law puede cambiar, mejorar o discontinuar características con aviso razonable, o sin aviso para mejora de seguridad o cumplimiento legal. Si se discontinúan versiones anteriores, se proporciona período de transición razonable.</p>

          <hr />

          <h2>19. Resolución de Disputas</h2>
          <p>Ambas partes se comprometen a resolver disputas de buena fe mediante comunicación directa y mediación si es insuficiente.</p>
          <p>Estos Términos se rigen por leyes de la República de Chile. Tribunal competente será el de domicilio de Billora Law en Santiago.</p>
          <p>Las partes pueden optar por arbitraje conforme a reglas de la Cámara de Comercio de Santiago.</p>

          <hr />

          <h2>20. Disposiciones Generales</h2>
          <p>Estos Términos constituyen el acuerdo completo entre las partes respecto al uso de la Plataforma y reemplazan cualquier acuerdo anterior.</p>
          <p>Si alguna disposición es inválida, las demás permanecen vigentes. La Firma no puede asignar derechos sin consentimiento. Billora Law puede asignar derechos a terceros con aviso razonable.</p>
          <p><strong>Secciones que sobreviven a terminación:</strong> Limitación de Responsabilidad (Sección 7), Propiedad Intelectual (Sección 5), Indemnización (Sección 16), Confidencialidad (Sección 14), Resolución de Disputas (Sección 19).</p>

          <hr />

          <h2>21. Contacto</h2>
          <p>Para consultas sobre estos Términos o reportar violaciones, contáctenos a través de los canales oficiales disponibles en billoralaw.com.</p>

          <hr />

          <p className="text-xs text-slate-400 mt-8">
            Estos Términos de Uso y Condiciones fueron actualizados en mayo de 2026 y cumplen con regulación de servicios digitales en Chile, Ley 19.628, Ley 19.496, Código Civil Chileno, y buenas prácticas internacionales.
          </p>
          <p className="text-xs text-slate-400">
            Al utilizar Billora Law, usted reconoce haber leído, entendido y aceptado estos Términos. Agradecemos su confianza en Billora Law.
          </p>
        </div>
      </main>

      <footer className="py-8 px-6 border-t border-slate-100 mt-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between text-sm text-slate-400">
          <span>Billora Law - 2026</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-slate-600 transition-colors">Privacidad</Link>
            <Link to="/landing" className="hover:text-slate-600 transition-colors">Volver al inicio</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
